import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { MoodCard } from "./MoodCard";
import { getCardOfTheDay } from "../lib/api";

/**
 * CardOfTheDay — one real, recent, opened card surfaced on Home.
 *
 * Renders nothing until the fetch resolves. On null payload (fresh
 * DB, no opens yet), renders nothing. On payload, renders an eyebrow
 * "TODAY'S CARD", a MoodCard preview, attribution chip, and an
 * "open" affordance that routes to /open/<slug> (full sleeve intro).
 *
 * Two layout modes:
 *   - variant="desktop-aside" — replaces the static decorative aside
 *     on lg+ screens. Sticky-friendly, 300px wide.
 *   - variant="mobile-tile"   — slim full-width row for mobile. Hidden
 *     on lg+ to avoid duplication.
 */
export default function CardOfTheDay({ variant = "desktop-aside" }) {
    const navigate = useNavigate();
    const [payload, setPayload] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        getCardOfTheDay()
            .then((data) => {
                if (cancelled) return;
                setPayload(data);
                setLoaded(true);
            })
            .catch(() => {
                if (!cancelled) setLoaded(true);
            });
        return () => { cancelled = true; };
    }, []);

    if (!loaded || !payload?.card || !payload?.slug) return null;

    const open = () => navigate(`/open/${payload.slug}`);
    const eyebrow = payload.is_editor_pick ? "editor's pick · today" : "today's card";
    const attribution = payload.author_first_name
        ? `quietly from ${payload.author_first_name}`
        : "quietly from someone";

    if (variant === "mobile-tile") {
        return (
            <motion.button
                type="button"
                onClick={open}
                data-testid="card-of-the-day-mobile"
                className="lg:hidden btn-press mt-12 mb-2 w-full text-left rounded-3xl border border-white/10 bg-surface-1/60 backdrop-blur-sm p-5 transition-colors hover:border-amber-200/30 group"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -2 }}
            >
                <div className="flex items-center justify-between mb-5">
                    <span
                        className="font-sans text-[9px] uppercase tracking-[0.42em] text-amber-200/85 flex items-center gap-2"
                        data-testid="card-of-the-day-eyebrow-mobile"
                    >
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-200/80 animate-pulse" />
                        {eyebrow}
                    </span>
                    <ArrowUpRight className="w-3.5 h-3.5 text-app-3 group-hover:text-amber-200/85 transition-colors" />
                </div>
                <div className="flex justify-center mb-4">
                    <div className="w-[260px] rounded-2xl overflow-hidden shadow-2xl">
                        <MoodCard
                            card={payload.card}
                            moodId={payload.mood_id || null}
                            showWatermark={true}
                            lane={payload.lane}
                            temperature={null}
                        />
                    </div>
                </div>
                <div className="flex items-center justify-between gap-3 px-1">
                    <span
                        className="font-sans text-[9px] uppercase tracking-[0.32em] text-app-2"
                        data-testid="card-of-the-day-author-mobile"
                    >
                        {attribution}
                    </span>
                    <span className="font-sans text-[9px] uppercase tracking-[0.32em] text-app-3 inline-flex items-center gap-1 group-hover:text-amber-200/85 transition-colors">
                        open
                        <ArrowUpRight className="w-3 h-3" />
                    </span>
                </div>
                {payload.lane && (
                    <p
                        className="mt-2 px-1 font-sans text-[9px] uppercase tracking-[0.42em] text-app-3 opacity-65"
                        data-testid="card-of-the-day-lane-mobile"
                    >
                        lane · {payload.lane}
                    </p>
                )}
            </motion.button>
        );
    }

    // Default: desktop-aside
    return (
        <motion.aside
            data-testid="card-of-the-day-desktop"
            className="hidden lg:flex lg:flex-col lg:items-center lg:justify-start lg:row-start-1 lg:col-start-2 lg:sticky lg:top-24"
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        >
            <button
                type="button"
                onClick={open}
                className="btn-press group w-[300px] text-left transition-transform hover:-translate-y-1"
                data-testid="card-of-the-day-open-button"
            >
                <div
                    className="font-sans text-[10px] uppercase tracking-[0.42em] text-amber-200/85 mb-4 flex items-center gap-2"
                    data-testid="card-of-the-day-eyebrow"
                >
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-200/80 animate-pulse" />
                    {eyebrow}
                </div>
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                    <MoodCard
                        card={payload.card}
                        moodId={payload.mood_id || null}
                        showWatermark={true}
                        lane={payload.lane}
                        temperature={null}
                    />
                </div>
                <div className="mt-5 flex items-center justify-between gap-3">
                    <span
                        className="font-sans text-[10px] uppercase tracking-[0.32em] text-app-2"
                        data-testid="card-of-the-day-author"
                    >
                        {attribution}
                    </span>
                    <span className="font-sans text-[10px] uppercase tracking-[0.32em] text-app-3 inline-flex items-center gap-1 group-hover:text-amber-200/85 transition-colors">
                        open
                        <ArrowUpRight className="w-3 h-3" />
                    </span>
                </div>
                {payload.lane && (
                    <p
                        className="mt-2 font-sans text-[9px] uppercase tracking-[0.42em] text-app-3 opacity-65"
                        data-testid="card-of-the-day-lane"
                    >
                        lane · {payload.lane}
                    </p>
                )}
            </button>
        </motion.aside>
    );
}
